import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import useToggle from 'hooks/useToggle'
import { skip, take } from 'lib/array/arrayUtils'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Expander from 'components/utils/Expander'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FilterPanelCheckboxGroupItem from './FilterPanelCheckboxGroupItem'
import type { FilterPanelCheckboxDensity } from './FilterPanelCheckboxGroupItem'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Switch from 'LuxKit/Switch/Switch'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import { useIntl } from 'react-intl'
import type { FilterPanelCheckItem } from './FilterPanelCheckboxGroup.types'
import { rem } from 'polished'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { encodeFilterPanelSet } from './filterPanelSetUtils'

export type { FilterPanelCheckItem } from './FilterPanelCheckboxGroup.types'
export type { FilterPanelCheckboxDensity } from './FilterPanelCheckboxGroupItem'
export {
  FILTER_PANEL_SET_PREFIX,
  encodeFilterPanelSet,
  tryDecodeFilterPanelSet,
} from './filterPanelSetUtils'

const CheckboxGroup = styled(VerticalSpacer)<{ $density: FilterPanelCheckboxDensity }>`
  margin-top: ${props => props.$density === 'compact' ? 0 : rem(6)};
`

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SelectAllRow = styled(Group)`
  height: ${rem(32)};
`

interface Props {
  name: string
  items: Array<FilterPanelCheckItem>
  defaultSelected?: Set<string | number>
  selected?: Set<string | number>
  maxItems?: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  titlecase?: boolean
  allowShowMore?: boolean
  allowCheckAll?: boolean
  selectAllMode?: 'none' | 'checkbox' | 'switch'
  selectAllLabel?: string
  showMoreWithCount?: boolean
  showOnly?: boolean
  onOnlyClick?: (value: string | number) => void
  luxPlusValues?: Set<string | number>
  loadingValues?: boolean
  maxValue?: number
  highlightLowestPrice?: boolean
  /**
   * `compact` opts into the flights redesign row layout. Other verticals keep
   * the legacy checkbox styling unless they pass this explicitly.
   */
  density?: FilterPanelCheckboxDensity
}

function FilterPanelCheckboxGroup(props: Props) {
  const {
    onChange,
    name,
    items,
    maxItems = Infinity,
    defaultSelected,
    selected,
    titlecase = false,
    allowShowMore = true,
    allowCheckAll = false,
    selectAllMode = 'none',
    selectAllLabel,
    showMoreWithCount = false,
    showOnly = false,
    onOnlyClick,
    luxPlusValues,
    loadingValues,
    maxValue = 1000,
    highlightLowestPrice = false,
    density = 'default',
  } = props
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const [showMore, toggleShowMore] = useToggle(!allowShowMore)
  const visibleItems = useMemo(() => items.filter(item => !item.hidden), [items])
  const hasMore = visibleItems.length > maxItems

  const lowestPrice = useMemo(() => {
    if (!highlightLowestPrice) {
      return undefined
    }

    const prices = visibleItems
      .map(item => item.price)
      .filter((price): price is number => price !== undefined)

    if (!prices.length) {
      return undefined
    }

    return Math.min(...prices)
  }, [highlightLowestPrice, visibleItems])

  const isAllSelected = useMemo(() => {
    if (selected?.has('all')) {
      return true
    }
    if (!selected?.size) {
      return false
    }
    return visibleItems.length > 0 && visibleItems.every(item => selected.has(item.value))
  }, [selected, visibleItems])

  const onCheckAll = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked

    onChange?.({
      ...e,
      target: {
        ...e.target,
        name,
        value: checked ? 'all' : '',
      },
    })
  }, [name, onChange])

  const onSelectAllSwitch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.({
      ...e,
      target: {
        ...e.target,
        name,
        value: e.target.checked ? 'all' : '',
      },
    })
  }, [name, onChange])

  const allLabel = selectAllLabel ?? intl.formatMessage({
    id: '4AnY0LVHfJOfBTaRm8Sz',
    defaultMessage: 'All',
  })
  const showMoreLabel = showMore
    ? intl.formatMessage({
        id: 'pwKWEC255CCTQ95WgCu8',
        defaultMessage: 'Show less',
      })
    : showMoreWithCount
      ? intl.formatMessage({
          id: 'NEvX3rH98XUhdyypiHeB',
          defaultMessage: 'Show all {count}',
        }, { count: visibleItems.length })
      : intl.formatMessage({
          id: '23D7PyGMWMhoe37SIUVQ',
          defaultMessage: 'Show more',
        })

  const isItemSelected = useCallback((value: string | number) => {
    if (!selected) {
      return undefined
    }

    // 'all' sentinel means every option is selected (e.g. airlines with no filter applied)
    if (selected.has('all')) {
      return true
    }

    return selected.has(value)
  }, [selected])

  const handleItemChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) {
      return
    }

    // Empty filter state is shown as selected=['all'].
    if (selected?.has('all') && !e.target.checked) {
      // Airlines/Stopover: on mobile there is no hover "Only" pill, so tapping one
      // option from the default all-selected state should select that option only.
      if (isMobile && onOnlyClick) {
        onOnlyClick(e.target.value)
        return
      }

      // Elsewhere, unchecking one option expands to every other visible option.
      // (A normal toggle would ADD the clicked value to [], selecting only that one.)
      const remaining = visibleItems
        .map(item => String(item.value))
        .filter(value => value !== e.target.value)

      onChange({
        ...e,
        target: {
          ...e.target,
          name,
          value: encodeFilterPanelSet(remaining),
        },
      })
      return
    }

    onChange(e)
  }, [isMobile, name, onChange, onOnlyClick, selected, visibleItems])

  const renderItem = useCallback((item: FilterPanelCheckItem) => (
    <FilterPanelCheckboxGroupItem
      key={item.value}
      item={item}
      name={name}
      onChange={handleItemChange}
      defaultChecked={item.defaultChecked ?? defaultSelected?.has(item.value)}
      checked={item.checked ?? isItemSelected(item.value)}
      titlecase={titlecase}
      loadingValue={loadingValues}
      hideCheckbox={item.hideCheckbox}
      maxValue={maxValue}
      density={density}
      showOnly={showOnly}
      onOnlyClick={onOnlyClick}
      luxPlusLabel={luxPlusValues?.has(item.value)}
      highlightLowestPrice={highlightLowestPrice}
      lowestPrice={lowestPrice}
    />
  ), [
    defaultSelected,
    density,
    handleItemChange,
    highlightLowestPrice,
    isItemSelected,
    loadingValues,
    lowestPrice,
    luxPlusValues,
    maxValue,
    name,
    onOnlyClick,
    showOnly,
    titlecase,
  ])

  // Preserve legacy `allowCheckAll` without requiring callers to set selectAllMode.
  const effectiveSelectAllMode = selectAllMode !== 'none'
    ? selectAllMode
    : allowCheckAll
      ? 'checkbox'
      : 'none'

  return (
    <CheckboxGroup gap={8} $density={density}>
      {effectiveSelectAllMode === 'checkbox' && <CheckboxInput
        key="checkAll"
        name="All"
        value="All"
        size="medium"
        onChange={onCheckAll}
        checked={isAllSelected}
      >
        {allLabel}
      </CheckboxInput>}
      {effectiveSelectAllMode === 'switch' && <SelectAllRow direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <BodyText variant="medium">{allLabel}</BodyText>
        <Switch
          checked={isAllSelected}
          onChange={onSelectAllSwitch}
        />
      </SelectAllRow>}
      {density === 'compact' ? (
        <CheckboxList>
          {take(visibleItems, maxItems).map(renderItem)}
          {hasMore && <Expander expanded={showMore}>
            {skip(visibleItems, maxItems).map(renderItem)}
          </Expander>}
        </CheckboxList>
      ) : (
        <>
          {take(visibleItems, maxItems).map(renderItem)}
          {hasMore && <Expander expanded={showMore}>
            <VerticalSpacer gap={8}>
              {skip(visibleItems, maxItems).map(renderItem)}
            </VerticalSpacer>
          </Expander>}
        </>
      )}
      {hasMore && <>
        {allowShowMore && (density === 'compact' && showMoreWithCount
          ? (
              <TextLink
                size="medium"
                weight="bold"
                onClick={toggleShowMore}
                endIcon={<LineAngleDownIcon />}
              >
                {showMoreLabel}
              </TextLink>
            )
          : (
              <SmallToggleButton
                on={showMore}
                onClick={toggleShowMore}
                size="medium"
                iconPosition="start"
              >
                {showMoreLabel}
              </SmallToggleButton>
            ))}
      </>}
    </CheckboxGroup>

  )
}

export default React.memo(FilterPanelCheckboxGroup)
